/**
 * Route access (03 §0.3, 06 §1.3): a guest who opens a user route goes to `/login?next=<path>`; a user who opens
 * `/admin/*` gets the 404 page (AC-ROLE-03). Until the first probe answers, nothing renders.
 */
import { t } from '@tarock/i18n';
import type { ComponentType } from 'preact';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { NotFound } from '../routes/notFound';
import { authStore } from '../stores/auth';

function Waiting() {
  return (
    <main class="page" id="main" aria-busy="true">
      <p>{t('common.loading')}</p>
    </main>
  );
}

function guarded<P extends object>(C: ComponentType<P>, admin: boolean): ComponentType<P> {
  return function Guarded(props: P) {
    const status = authStore.status.value;
    const user = authStore.user.value;
    const { url, route } = useLocation();
    const out = status === 'signedOut' || status === 'twoFactorPending';
    useEffect(() => {
      if (out && !url.startsWith('/login')) {
        route(`/login?next=${encodeURIComponent(url)}`, true);
      }
    }, [out, url]);
    if (status === 'unknown') {
      return navigator.onLine ? <Waiting /> : <NotFound />;
    }
    if (!user) {
      return <Waiting />;
    }
    if (admin && user.role !== 'admin') {
      return <NotFound />;
    }
    return <C {...props} />;
  };
}

/** Wraps a user route. */
export const userRoute = <P extends object>(C: ComponentType<P>): ComponentType<P> => guarded(C, false);
/** Wraps an admin route. */
export const adminRoute = <P extends object>(C: ComponentType<P>): ComponentType<P> => guarded(C, true);
